import type { JSX } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { JobPosition } from '../types/jobPosition.types';
import { JobPositionStatusBadge } from './JobPositionStatusBadge';
import { JobPositionActions } from './JobPositionActions';

export interface JobPositionCardProps {
  jobPosition: JobPosition;
  onEdit: (jobPosition: JobPosition) => void;
  onDelete: (id: string) => void;
  onArchive: (id: string, archive: boolean) => void;
}

export function JobPositionCard({
  jobPosition,
  onEdit,
  onDelete,
  onArchive,
}: JobPositionCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="flex flex-col gap-1 flex-1">
          <h3 className="font-semibold text-lg">{jobPosition.title}</h3>
          <p className="text-sm text-muted-foreground">{jobPosition.department}</p>
        </div>
        <div className="flex items-center gap-2">
          <JobPositionStatusBadge status={jobPosition.status} />
          <JobPositionActions
            jobPosition={jobPosition}
            onEdit={onEdit}
            onDelete={onDelete}
            onArchive={onArchive}
          />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-2">
          <p className="text-sm line-clamp-2">{jobPosition.description}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              {jobPosition.location}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {jobPosition.employmentType.replace('_', ' ')}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {jobPosition.remotePolicy.replace('_', ' ')}
            </Badge>
            {jobPosition.experienceLevels.map((level) => (
              <Badge key={level} variant="outline" className="text-xs">
                {level}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
